import React, { useEffect, useState } from "react";
import { Plus, Pencil, X, Phone, Mail, Building2 } from "lucide-react";
import { toast }            from "../../../ui/Toast";
import { useConfirmDelete } from "../../../../hooks/useConfirmDelete";

const API     = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card    = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls= "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls= "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const EMPTY = {
  department: "", name: "", title: "", email: "", phone: "", mobile: "",
  location: "", is_emergency: false, notes: "",
};

const CATEGORIES = ["All","HR","Finance","IT","Legal","Operations","Payroll","Facilities","Emergency"];

export default function ContactsTab() {
  const [contacts,  setContacts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [draft,     setDraft]     = useState(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [filter,    setFilter]    = useState("All");
  const [msg,       setMsg]       = useState(null);
  const { confirmDelete, ConfirmDialog } = useConfirmDelete();
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(Array.isArray(await res.json()) ? await res.json() : []);
    } catch { setContacts([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!draft.department || !draft.name) return;
    setSaving(true); setMsg(null);
    try {
      const isNew = !editing;
      const res = await fetch(
        isNew ? `${API}/api/admin/contacts` : `${API}/api/admin/contacts/${editing}`,
        { method: isNew ? "POST" : "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(draft) }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg({ type: "success", text: "✓ Saved." });
      setShowForm(false); setDraft(EMPTY); setEditing(null);
      await load();
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSaving(false); }
  };

  const del = (id) => confirmDelete("this contact", async () => {
    try {
      const res = await fetch(`${API}/api/admin/contacts/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setContacts(c => c.filter(x => x.id !== id));
      toast.success("Contact deleted");
    } catch {
      toast.error("Failed to delete contact");
    }
  });

  const startEdit = (c) => { setDraft({ ...c }); setEditing(c.id); setShowForm(true); };
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const visible = filter === "All" ? contacts
    : filter === "Emergency" ? contacts.filter(c => c.is_emergency)
    : contacts.filter(c => c.department === filter);

  return (
    <div className="space-y-5">

      {/* Form */}
      {showForm && (
        <div className={`${card} p-5 space-y-4`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
            {editing ? "Edit Contact" : "New Contact"}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Department</label>
              <input className={inputCls} value={draft.department} onChange={e => set("department", e.target.value)} placeholder="HR, IT, Finance…" />
            </div>
            <div>
              <label className={labelCls}>Contact Name</label>
              <input className={inputCls} value={draft.name} onChange={e => set("name", e.target.value)} placeholder="Full name" />
            </div>
            <div>
              <label className={labelCls}>Job Title</label>
              <input className={inputCls} value={draft.title || ""} onChange={e => set("title", e.target.value)} placeholder="HR Manager" />
            </div>
            <div>
              <label className={labelCls}>Location / Office</label>
              <input className={inputCls} value={draft.location || ""} onChange={e => set("location", e.target.value)} placeholder="London, Bangalore…" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" className={inputCls} value={draft.email || ""} onChange={e => set("email", e.target.value)} placeholder="hr@company.com" />
            </div>
            <div>
              <label className={labelCls}>Phone (office)</label>
              <input className={inputCls} value={draft.phone || ""} onChange={e => set("phone", e.target.value)} placeholder="+44 20 1234 5678" />
            </div>
            <div>
              <label className={labelCls}>Mobile</label>
              <input className={inputCls} value={draft.mobile || ""} onChange={e => set("mobile", e.target.value)} placeholder="+44 7700 000000" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-white/50">
                <input type="checkbox" checked={!!draft.is_emergency} onChange={e => set("is_emergency", e.target.checked)} className="w-4 h-4 accent-[#00ff90]" />
                Emergency contact
              </label>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Notes</label>
              <input className={inputCls} value={draft.notes || ""} onChange={e => set("notes", e.target.value)} placeholder="e.g. Available Mon–Fri 9am–5pm" />
            </div>
          </div>
          {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
          <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
            <button onClick={() => { setShowForm(false); setDraft(EMPTY); setEditing(null); }}
              className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving || !draft.name || !draft.department}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Saving…" : "Save Contact"}
            </button>
          </div>
        </div>
      )}

      {/* Contact list */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between flex-wrap gap-3">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Department Contacts</span>
          <div className="flex items-center gap-3">
            {/* Category filter */}
            <div className="flex gap-1 flex-wrap">
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setFilter(c)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    filter === c ? "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/30 hover:border-white/20 hover:text-white/60"
                  }`}>{c}</button>
              ))}
            </div>
            <button onClick={() => { setDraft(EMPTY); setEditing(null); setShowForm(v => !v); }}
              className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
              <Plus size={12} /> Add
            </button>
          </div>
        </div>

        <div className="divide-y divide-white/5">
          {loading ? Array.from({length:3}).map((_,i) => (
            <div key={i} className="px-5 py-4"><div className="h-12 rounded-lg bg-white/5 animate-pulse" /></div>
          )) : visible.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No contacts found.</p>
          ) : visible.map(c => (
            <div key={c.id} className={`flex items-start justify-between gap-3 px-5 py-4 hover:bg-white/3 transition-colors ${c.is_emergency ? "border-l-2 border-red-500/40" : ""}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-sm text-white/80 font-medium">{c.name}</p>
                  {c.title && <span className="text-xs text-white/40">{c.title}</span>}
                  <span className="text-xs px-2 py-0.5 rounded-full border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60">
                    {c.department}
                  </span>
                  {c.is_emergency && (
                    <span className="text-xs px-2 py-0.5 rounded-full border border-red-500/30 bg-red-500/10 text-red-400">Emergency</span>
                  )}
                  {c.location && <span className="text-xs text-white/25">{c.location}</span>}
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-xs text-[#00ff90]/50 hover:text-[#00ff90] transition-colors">
                      <Mail size={11} /> {c.email}
                    </a>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors">
                      <Phone size={11} /> {c.phone}
                    </a>
                  )}
                  {c.mobile && (
                    <a href={`tel:${c.mobile}`} className="flex items-center gap-1.5 text-xs text-white/30 hover:text-white transition-colors">
                      <Phone size={11} /> {c.mobile}
                    </a>
                  )}
                </div>
                {c.notes && <p className="text-xs text-white/25 mt-1">{c.notes}</p>}
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => startEdit(c)}
                  className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                  <Pencil size={12} />
                </button>
                <button onClick={() => del(c.id)}
                  className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {ConfirmDialog}
    </div>
  );
}